<template>
  <div class="wizard-step-antes">
    <div class="step-header">
      <h3>
        <i class="pi pi-camera"></i>
        {{ t('Antes do serviço') }}:
      </h3>
      <h4>
        {{
          t('Nesta etapa você deve tirar uma foto de todos os itens ANTES de começar a limpeza')
        }}.
      </h4>
    </div>

    <!-- Progress bar -->
    <div class="progress-section">
      <p class="progress-text">
        {{ servicesWithBeforePhotos }} / {{ nonCanceledServices }}
        {{ t('serviços') }}
      </p>
      <progress-bar :value="progressPercentage" :showValue="false" />
    </div>

    <!-- Add service button -->
    <div class="action-buttons">
      <app-button
        v-if="canAddService"
        :label="t('Adicionar serviço')"
        icon="pi pi-plus"
        severity="secondary"
        size="small"
        data-testid="wizard-step1-antes-add-service-button"
        @click="handleAddService"
      />
    </div>

    <!-- Services table -->
    <ServicesListView
      :services="activeServices"
      mode="antes"
      :show-edit-link="true"
      @edit-service="handleEditService"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import ServicesListView from './ServicesListView.vue';
import { useWizardServicos } from '../../../composables/useWizardServicos';
import { eventBus } from '../../../eventBus';
import { log } from '../../../utils/DebugLogger';

interface Servico {
  compromisso_servico_status?: number;
  [key: string]: any;
}

interface Compromisso {
  compromisso_id: string | number;
  compromisso_orcamento?: string | number | null;
  servicos?: Servico[];
  [key: string]: any;
}

const { t } = useI18n();
const { countNonCanceledServices, countServicesWithBeforePhotos } = useWizardServicos();

const props = defineProps<{
  compromisso: Compromisso;
  canAddService?: boolean;
}>();

const emit = defineEmits<{
  (e: 'next-step'): void;
  (e: 'add-service'): void;
  (e: 'refresh'): void;
}>();

const activeServices = computed<Servico[]>(() => {
  if (!props.compromisso || !props.compromisso.servicos) return [];
  return props.compromisso.servicos.filter((s) => s.compromisso_servico_status !== 6);
});

const nonCanceledServices = computed<number>(() => {
  return countNonCanceledServices(props.compromisso as any);
});

const servicesWithBeforePhotos = computed<number>(() => {
  return countServicesWithBeforePhotos(props.compromisso as any);
});

const progressPercentage = computed<number>(() => {
  if (nonCanceledServices.value === 0) return 0;
  return (servicesWithBeforePhotos.value / nonCanceledServices.value) * 100;
});

const handleAddService = (): void => {
  log('WizardStep1Antes', 'handleAddService', 'Requesting to add service');
  emit('add-service');
};

const handleEditService = (servicoId: string | number): void => {
  log('WizardStep1Antes', 'handleEditService', 'Opening edit modal for service', servicoId);

  eventBus.emit('compromisso-servico.open-edit', {
    compromissoServicoId: servicoId,
    compromissoId: props.compromisso.compromisso_id,
    orcamentoId: props.compromisso.compromisso_orcamento || 0
  });
};

const handlePhotoUploaded = (data: any): void => {
  log('WizardStep1Antes', 'handlePhotoUploaded', 'Photo uploaded event received', data);
  emit('refresh');
};

onMounted(() => {
  log('WizardStep1Antes', 'mounted', 'Component mounted');
  eventBus.on('wizard-servicos.photo-uploaded', handlePhotoUploaded);
  eventBus.on('wizard-servicos.photo-deleted', handlePhotoUploaded);
});

onBeforeUnmount(() => {
  log('WizardStep1Antes', 'beforeUnmount', 'Cleaning up event listeners');
  eventBus.off('wizard-servicos.photo-uploaded', handlePhotoUploaded);
  eventBus.off('wizard-servicos.photo-deleted', handlePhotoUploaded);
});
</script>

<style scoped>
.wizard-step-antes {
  padding: 20px;
}

.step-header {
  margin-bottom: 30px;
}

.step-header h3 {
  font-size: 24px;
  margin-bottom: 10px;
}

.step-header h4 {
  font-size: 16px;
  color: #666;
  font-weight: normal;
}

.progress-section {
  margin-bottom: 20px;
}

.progress-text {
  font-size: 16px;
  margin-bottom: 10px;
  font-weight: 500;
}

.action-buttons {
  margin-bottom: 15px;
  text-align: right;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .wizard-step-antes {
    padding: 10px;
  }

  .step-header h3 {
    font-size: 20px;
  }

  .step-header h4 {
    font-size: 14px;
  }
}
</style>
